import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ecpChallengeApi, ecpRegisterCompanyApi } from "../api";
import { signChallengeWithEcp, ecpErrorMessage } from "../lib/ncalayer";
import { useAuth } from "../context/AuthContext";
import { normalizeApiError } from "../lib/apiError";

function _initialSuggest() {
  try {
    const raw = sessionStorage.getItem("ecp_suggest_company");
    if (raw) return JSON.parse(raw);
  } catch (_e) { /* ignore */ }
  return null;
}

/**
 * Баннер «У вас есть БИН-сертификат → одним кликом зарегистрировать компанию».
 * Появляется только когда:
 *  - В sessionStorage есть ecp_suggest_company (поставлено на ECP-логине)
 *  - У текущего пользователя НЕТ company_id
 */
export default function EcpCompanyBanner() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [suggest, setSuggest] = useState(_initialSuggest);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Скрываем, если компания уже создана или пользователь не подходит
  if (!suggest || !user || user.company_id) return null;

  const dismiss = () => {
    setSuggest(null);
    try { sessionStorage.removeItem("ecp_suggest_company"); } catch (_e) { /* ignore */ }
  };

  const register = async () => {
    setError("");
    setBusy(true);
    try {
      // Нужна свежая подпись для register-company
      const { challenge, challenge_id } = await ecpChallengeApi();
      const cms_b64 = await signChallengeWithEcp(challenge);
      await ecpRegisterCompanyApi(challenge_id, cms_b64, 5, 0);
      try { sessionStorage.removeItem("ecp_suggest_company"); } catch (_e) { /* ignore */ }
      await refresh();
      navigate("/dashboard");
      window.location.reload(); // Простой способ обновить весь UI после смены роли
    } catch (e) {
      setError(normalizeApiError(e, ecpErrorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="alert alert-primary border-0 shadow-sm mb-3" data-testid="ecp-company-banner"
         style={{ background: "linear-gradient(135deg, #e7f1ff 0%, #d1e7ff 100%)" }}>
      <div className="d-flex align-items-start gap-3">
        <div className="flex-shrink-0" style={{ fontSize: "2rem" }}>
          <i className="bi bi-building-check"></i>
        </div>
        <div className="flex-grow-1">
          <h6 className="alert-heading mb-1" data-testid="ecp-banner-title">
            Зарегистрировать компанию автоматически?
          </h6>
          <div className="small mb-2">
            В вашем сертификате ЭЦП обнаружена организация
            <strong data-testid="ecp-banner-org-name"> «{suggest.name}»</strong>
            {" "}(БИН <span data-testid="ecp-banner-bin">{suggest.bin}</span>).
            Создадим её в Tabel.kz за один клик — название, БИН и ФИО директора будут
            заполнены автоматически из сертификата.
          </div>
          {error && (
            <div className="small text-danger mb-2" data-testid="ecp-banner-error">{error}</div>
          )}
          <div className="d-flex gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={register}
              disabled={busy}
              data-testid="ecp-banner-register"
            >
              {busy ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  Подписание…
                </>
              ) : (
                <>
                  <i className="bi bi-shield-check me-1"></i>
                  Зарегистрировать одним кликом
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm text-muted"
              onClick={dismiss}
              disabled={busy}
              data-testid="ecp-banner-dismiss"
            >
              Не сейчас
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
